import jsPDF from 'jspdf';
import {
  AlignmentType,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  TextRun,
  convertInchesToTwip,
} from 'docx';
import {
  isSectionHeading,
  isListSectionHeading,
  isJdMetaLine,
  isSectionDetailLine,
  normalizeHeadingKey,
  normalizeListSectionBullets,
  prepareJdRichHtml,
} from './jdPreviewFormat';

/** PDF bullet layout — drawn circles (Helvetica cannot render Unicode • reliably). */
const PDF_BULLET_LAYOUT = {
  bulletXOffset: 4,
  textXOffset: 8,
  bulletRadius: 0.55,
  widthReduction: 10,
};

function pdfBulletTextWidth(contentWidth) {
  return contentWidth - PDF_BULLET_LAYOUT.widthReduction;
}

function pdfBulletCenterY(doc, textBaselineY) {
  return textBaselineY - pdfLineHeightMm(doc) * 0.32;
}

/** Feuji document export branding — aligned with Resume Formatter standards. */
export const FEUJI_BRAND = {
  headerOrangeRgb: [243, 118, 45],
  headerOrangeHex: '#F3762D',
  bodyTextHex: '#000000',
  sectionHeadingHex: '#000000',
  metaLabelHex: '#000000',
};

const LOGO_PATH = '/feuji-logo-header.png';

/** Spacing convention (mm in PDF, px-equivalent in Word). */
export const JD_EXPORT_SPACING = {
  gapMetaItem: 2,
  gapAfterMetaBlock: 5,
  gapBeforeSection: 5.5,
  gapAfterSectionHeading: 3,
  gapAfterParagraph: 2.5,
  gapBulletItem: 1.2,
  gapAfterBulletList: 3.5,
  gapBreak: 2,
};

export const JD_PDF_LAYOUT = {
  headerHeightMm: 8,
  footerMarginMm: 10,
  marginMm: 12,
  metaFontSize: 10,
  bodyFontSize: 10,
  headingFontSize: 11.5,
  lineHeightFactor: 1.22,
  ...JD_EXPORT_SPACING,
};

/** Matches Resume Formatter header bar (`height: 34px`, logo fills bar height). */
export const FEUJI_WORD_HEADER_HEIGHT_PX = 34;

/** Preload logo as base64 + aspect ratio for PDF/Word exports. */
export function loadFeujiLogoData() {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL('image/png'),
          aspectRatio: img.naturalWidth / img.naturalHeight,
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = LOGO_PATH;
  });
}

export function drawFeujiPdfHeader(pdf, logoData, headerHeightMm = 8) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const [r, g, b] = FEUJI_BRAND.headerOrangeRgb;

  pdf.setFillColor(r, g, b);
  pdf.rect(0, 0, pageWidth, headerHeightMm, 'F');

  if (logoData?.dataUrl && logoData.aspectRatio) {
    const logoH = headerHeightMm;
    const logoW = logoH * logoData.aspectRatio;
    const logoX = pageWidth - logoW - 1.5;
    pdf.addImage(logoData.dataUrl, 'PNG', logoX, 0, logoW, logoH);
  } else {
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bolditalic');
    pdf.text('feuji', pageWidth - 4, headerHeightMm / 2 + 1.2, { align: 'right' });
  }
}

function buildFeujiWordHeaderTableHtml(logoBase64, logoAspectRatio) {
  const orange = FEUJI_BRAND.headerOrangeHex;
  const barH = FEUJI_WORD_HEADER_HEIGHT_PX;
  const logoW = logoBase64 && logoAspectRatio ? Math.round(barH * logoAspectRatio) : null;

  const fallback =
    `<span style="color:#FFFFFF;font-size:14pt;font-family:Arial,sans-serif;font-weight:bold;font-style:italic;line-height:${barH}px;">feuji</span>`;

  const logoImg = logoBase64
    ? `<img src="${logoBase64}" alt="feuji" border="0" height="${barH}"${
        logoW ? ` width="${logoW}"` : ''
      } style="display:block;height:${barH}px;${logoW ? `width:${logoW}px;` : 'width:auto;'}border:0;margin:0;padding:0;" />`
    : fallback;

  return `<table class="feuji-word-header" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;margin:0 0 12px 0;width:100%;">
<tr style="height:${barH}px;mso-height-rule:exactly;">
<td bgcolor="${orange}" width="100%" style="background-color:${orange};padding:0 5px 0 0;text-align:right;vertical-align:middle;height:${barH}px;mso-height-rule:exactly;">
<table align="right" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;height:${barH}px;">
<tr style="height:${barH}px;mso-height-rule:exactly;">
<td style="padding:0;margin:0;vertical-align:middle;text-align:right;height:${barH}px;line-height:${barH}px;">
${logoImg}
</td>
</tr>
</table>
</td>
</tr>
</table>`;
}

/** Word-compatible Feuji header — right-aligned logo vertically centered in orange bar. */
export function buildFeujiWordHeaderHtml(logoBase64, logoAspectRatio) {
  return buildFeujiWordHeaderTableHtml(logoBase64, logoAspectRatio);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isMetaLine(line) {
  return isJdMetaLine(line);
}

export function classifyJdPdfLine(line) {
  const t = line.trim();
  if (!t) return 'empty';
  if (/^[-•·*]\s+/.test(t) || /^\d+[.)]\s+/.test(t)) return 'bullet';
  if (isMetaLine(t)) return 'meta';
  if (isSectionHeading(t)) return 'heading';
  return 'text';
}

/** Merge consecutive plain-text lines into paragraphs. */
export function preprocessJdLinesForExport(jdText) {
  const rawLines = String(jdText || '').replace(/\\n/g, '\n').split('\n');
  const output = [];
  let textBuffer = [];

  const flushText = () => {
    const merged = textBuffer.join(' ').replace(/\s+/g, ' ').trim();
    if (merged) output.push(merged);
    textBuffer = [];
  };

  for (const raw of rawLines) {
    const line = raw.trim();
    if (!line) {
      flushText();
      if (output.length && output[output.length - 1] !== '') output.push('');
      continue;
    }

    const kind = classifyJdPdfLine(line);
    if (kind === 'text') {
      textBuffer.push(line);
      continue;
    }

    flushText();
    output.push(line);
  }

  flushText();
  return output;
}

/**
 * Structure JD into typed blocks for consistent field spacing.
 * Types: meta | heading | paragraph | bullets | break
 */
export function structureJdBlocks(jdText) {
  const rawLines = String(jdText || '').replace(/\\n/g, '\n').split('\n');
  const blocks = [];
  let bulletItems = [];
  let paragraphBuffer = [];
  let activeListSection = null;

  const flushBullets = () => {
    if (bulletItems.length) {
      blocks.push({ type: 'bullets', items: [...bulletItems] });
      bulletItems = [];
    }
  };

  const flushParagraph = () => {
    const merged = paragraphBuffer.join(' ').replace(/\s+/g, ' ').trim();
    if (merged) blocks.push({ type: 'paragraph', content: merged });
    paragraphBuffer = [];
  };

  const pushBullet = (text) => {
    flushParagraph();
    bulletItems.push(text);
  };

  const pushListSectionItems = (line) => {
    normalizeListSectionBullets(line, activeListSection).forEach((item) => pushBullet(item));
  };

  for (const raw of rawLines) {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      flushParagraph();
      blocks.push({ type: 'break' });
      continue;
    }

    const markdownHeading = line.match(/^\*\*(.+?)\*\*:?\s*$/);
    if (markdownHeading && isSectionHeading(markdownHeading[1])) {
      flushBullets();
      flushParagraph();
      activeListSection = isListSectionHeading(markdownHeading[1])
        ? normalizeHeadingKey(markdownHeading[1])
        : null;
      blocks.push({ type: 'heading', content: markdownHeading[1].trim().replace(/:$/, '') });
      continue;
    }

    if (isSectionHeading(line)) {
      flushBullets();
      flushParagraph();
      activeListSection = isListSectionHeading(line) ? normalizeHeadingKey(line) : null;
      blocks.push({ type: 'heading', content: line.trim().replace(/:$/, '') });
      continue;
    }

    if (activeListSection) {
      if (/^[-•·*]\s+/.test(line) || /^\d+[.)]\s+/.test(line)) {
        pushBullet(line.replace(/^[-•·*]\s+/, '').replace(/^\d+[.)]\s+/, ''));
        continue;
      }
      pushListSectionItems(line);
      continue;
    }

    if (isMetaLine(line) && !isSectionDetailLine(line)) {
      flushBullets();
      flushParagraph();
      activeListSection = null;
      blocks.push({ type: 'meta', content: line });
      continue;
    }

    if (/^[-•·*]\s+/.test(line) || /^\d+[.)]\s+/.test(line)) {
      pushBullet(line.replace(/^[-•·*]\s+/, '').replace(/^\d+[.)]\s+/, ''));
      continue;
    }

    flushBullets();
    paragraphBuffer.push(line);
  }

  flushBullets();
  flushParagraph();
  return blocks;
}

function gapBeforeBlock(block, prevBlock, layout) {
  if (!prevBlock || block.type === 'break') return 0;

  if (block.type === 'section') {
    return gapBeforeBlock({ type: 'heading', content: block.heading }, prevBlock, layout);
  }

  if (block.type === 'heading') {
    if (prevBlock.type === 'meta') return layout.gapAfterMetaBlock;
    if (prevBlock.type === 'break') return layout.gapBeforeSection * 0.5;
    // Tighter spacing when one section follows another (e.g. Skills → Education).
    if (prevBlock.type === 'section') return layout.gapAfterBulletList;
    return layout.gapBeforeSection;
  }

  if (block.type === 'paragraph' && prevBlock.type === 'heading') {
    return layout.gapAfterSectionHeading;
  }

  if (block.type === 'bullets' && prevBlock.type === 'heading') {
    return layout.gapAfterSectionHeading;
  }

  if (block.type === 'paragraph' && prevBlock.type === 'bullets') {
    return layout.gapAfterBulletList;
  }

  if (block.type === 'bullets' && prevBlock.type === 'paragraph') {
    return layout.gapAfterParagraph;
  }

  if (block.type === 'heading' && (prevBlock.type === 'paragraph' || prevBlock.type === 'bullets')) {
    return layout.gapBeforeSection;
  }

  return 0;
}

function gapAfterBlock(block, nextBlock, layout) {
  if (block.type === 'break') return layout.gapBreak;
  if (!nextBlock) return 0;

  if (block.type === 'meta' && nextBlock.type === 'meta') {
    return layout.gapMetaItem;
  }

  if (block.type === 'paragraph' && nextBlock.type === 'paragraph') {
    return layout.gapAfterParagraph;
  }

  if (block.type === 'bullets' || (block.type === 'section' && block.content?.type === 'bullets')) {
    if (nextBlock.type === 'section' || nextBlock.type === 'heading') {
      return 0;
    }
    return layout.gapAfterBulletList;
  }

  if (block.type === 'section') {
    return gapAfterBlock(block.content, nextBlock, layout);
  }

  return 0;
}

function pdfLineHeightMm(doc) {
  return (doc.getFontSize() * doc.getLineHeightFactor()) / doc.internal.scaleFactor;
}

function pdfLineHeightForFontSize(doc, fontSize) {
  const prev = doc.getFontSize();
  doc.setFontSize(fontSize);
  const lh = pdfLineHeightMm(doc);
  doc.setFontSize(prev);
  return lh;
}

function estimateHeadingHeight(doc, content, contentWidth, layout) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(layout.headingFontSize);
  const rows = doc.splitTextToSize(content, contentWidth);
  const lh = pdfLineHeightMm(doc);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(layout.bodyFontSize);
  return rows.length * lh;
}

function findNextSubstantiveBlockIndex(blocks, startIndex) {
  for (let i = startIndex; i < blocks.length; i += 1) {
    if (blocks[i].type !== 'break') return i;
  }
  return -1;
}

/** Merge section headings with content and drop blank-line break blocks. */
function normalizePdfBlocks(blocks) {
  const filtered = blocks.filter((block) => block.type !== 'break');
  return coalescePdfBlocks(filtered);
}

function coalescePdfBlocks(blocks) {
  const out = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.type === 'heading') {
      const nextIdx = findNextSubstantiveBlockIndex(blocks, i + 1);
      if (nextIdx !== -1) {
        const next = blocks[nextIdx];
        if (next.type === 'bullets' || next.type === 'paragraph') {
          out.push({ type: 'section', heading: block.content, content: next });
          i = nextIdx + 1;
          continue;
        }
      }
    }
    out.push(block);
    i += 1;
  }
  return out;
}

function estimateBlockHeight(doc, block, contentWidth, layout) {
  if (block.type === 'break') return layout.gapBreak;

  if (block.type === 'meta') {
    doc.setFontSize(layout.metaFontSize);
    const idx = block.content.indexOf(':');
    const value = block.content.slice(idx + 1).trim();
    const rows = doc.splitTextToSize(value || ' ', contentWidth - 14);
    doc.setFontSize(layout.bodyFontSize);
    return rows.length * pdfLineHeightMm(doc);
  }

  if (block.type === 'heading') {
    return estimateHeadingHeight(doc, block.content, contentWidth, layout);
  }

  if (block.type === 'paragraph') {
    doc.setFontSize(layout.bodyFontSize);
    const rows = doc.splitTextToSize(block.content, contentWidth);
    return rows.length * pdfLineHeightMm(doc);
  }

  if (block.type === 'bullets') {
    doc.setFontSize(layout.bodyFontSize);
    const textWidth = pdfBulletTextWidth(contentWidth);
    const body = block.items.reduce((sum, item, i) => {
      const rows = doc.splitTextToSize(item, textWidth);
      const itemH = rows.length * pdfLineHeightMm(doc);
      const gap = i < block.items.length - 1 ? layout.gapBulletItem : 0;
      return sum + itemH + gap;
    }, 0);
    return body;
  }

  return 0;
}

function isContentBlock(block) {
  return block && (block.type === 'bullets' || block.type === 'paragraph');
}

function estimateSingleBulletHeight(doc, item, contentWidth, layout, includeItemGap = true) {
  doc.setFontSize(layout.bodyFontSize);
  const rows = doc.splitTextToSize(item, pdfBulletTextWidth(contentWidth));
  const h = rows.length * pdfLineHeightMm(doc);
  return h + (includeItemGap ? layout.gapBulletItem : 0);
}

function estimateFirstContentHeight(doc, block, contentWidth, layout) {
  if (!block) return 0;
  if (block.type === 'bullets' && block.items?.length) {
    doc.setFontSize(layout.bodyFontSize);
    const rows = doc.splitTextToSize(block.items[0], pdfBulletTextWidth(contentWidth));
    return rows.length ? pdfLineHeightMm(doc) : 0;
  }
  if (block.type === 'paragraph') {
    doc.setFontSize(layout.bodyFontSize);
    const rows = doc.splitTextToSize(block.content, contentWidth);
    return rows.length ? pdfLineHeightMm(doc) : 0;
  }
  return 0;
}

/** Minimum height to keep a section heading with its first content line/item. */
function estimateHeadingWithFirstContent(doc, headingText, contentBlock, prevBlock, contentWidth, layout) {
  const pseudoHeading = { type: 'heading', content: headingText };
  const gapBefore = gapBeforeBlock(pseudoHeading, prevBlock, layout);
  const headingH = estimateHeadingHeight(doc, headingText, contentWidth, layout);
  const contentGap = contentBlock ? gapBeforeBlock(contentBlock, pseudoHeading, layout) : 0;
  const firstContentH = estimateFirstContentHeight(doc, contentBlock, contentWidth, layout);
  return gapBefore + headingH + contentGap + firstContentH;
}

/** Reserve height at page bottom so the next section can follow without a blank gap. */
function computeNextSectionReserve(doc, nextBlock, currentBlock, contentWidth, layout) {
  if (!nextBlock || nextBlock.type !== 'section') return 0;
  return estimateHeadingWithFirstContent(
    doc,
    nextBlock.heading,
    nextBlock.content,
    currentBlock,
    contentWidth,
    layout,
  );
}

function renderSectionBlock(
  doc,
  sectionBlock,
  prevBlock,
  nextBlock,
  margin,
  y,
  contentWidth,
  layout,
  maxY,
  startNewPage,
  contentTop,
  nextSectionReserve = 0,
) {
  const { heading, content } = sectionBlock;
  const pseudoHeading = { type: 'heading', content: heading };
  const gapBefore = gapBeforeBlock(pseudoHeading, prevBlock, layout);
  const togetherH = estimateHeadingWithFirstContent(
    doc, heading, content, prevBlock, contentWidth, layout,
  );

  if (y + togetherH > maxY) startNewPage();

  y += gapBefore;
  y = renderHeadingBlock(doc, heading, margin, y, contentWidth, layout);

  const contentGap = gapBeforeBlock(content, pseudoHeading, layout);

  if (content.type === 'bullets') {
    y = renderBulletsPaginated(
      doc,
      content.items,
      margin,
      y,
      contentWidth,
      layout,
      maxY,
      startNewPage,
      contentTop,
      contentGap,
      nextSectionReserve,
    );
  } else {
    y = renderParagraphPaginated(
      doc,
      content.content,
      margin,
      y,
      contentWidth,
      layout,
      maxY,
      startNewPage,
      contentTop,
      contentGap,
      nextSectionReserve,
    );
  }

  return y + gapAfterBlock(content, nextBlock, layout);
}

function renderSingleBulletItem(
  doc,
  item,
  margin,
  y,
  contentWidth,
  layout,
  maxY,
  startNewPage,
  contentTop,
  pageBottom = maxY,
) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(layout.bodyFontSize);
  doc.setTextColor(0, 0, 0);
  doc.setFillColor(0, 0, 0);

  const textX = margin + PDF_BULLET_LAYOUT.textXOffset;
  const bulletX = margin + PDF_BULLET_LAYOUT.bulletXOffset;
  const textWidth = pdfBulletTextWidth(contentWidth);
  const lineHeight = pdfLineHeightMm(doc);
  const rows = doc.splitTextToSize(item, textWidth);

  let cy = y;
  rows.forEach((row, rowIndex) => {
    if (cy + lineHeight > pageBottom) {
      startNewPage();
      cy = contentTop;
    }
    if (rowIndex === 0) {
      doc.circle(bulletX, pdfBulletCenterY(doc, cy), PDF_BULLET_LAYOUT.bulletRadius, 'F');
    }
    doc.text(row, textX, cy);
    cy += lineHeight;
  });

  return cy;
}

function renderBulletsPaginated(
  doc,
  items,
  margin,
  y,
  contentWidth,
  layout,
  maxY,
  startNewPage,
  contentTop,
  initialGapBefore = 0,
  reserveBottom = 0,
) {
  let cy = y;
  let pendingGapBefore = initialGapBefore;
  const lineHeight = pdfLineHeightMm(doc);
  const pageBottom = Math.max(
    contentTop + lineHeight,
    maxY - Math.max(0, reserveBottom),
  );

  items.forEach((item, itemIndex) => {
    const includeItemGap = itemIndex < items.length - 1;
    const itemHeight = estimateSingleBulletHeight(doc, item, contentWidth, layout, includeItemGap);

    if (cy + pendingGapBefore + itemHeight > pageBottom && cy > contentTop + 0.5) {
      startNewPage();
      cy = contentTop;
      pendingGapBefore = 0;
    }

    cy += pendingGapBefore;
    pendingGapBefore = 0;
    cy = renderSingleBulletItem(
      doc, item, margin, cy, contentWidth, layout, maxY, startNewPage, contentTop, pageBottom,
    );
    if (includeItemGap) cy += layout.gapBulletItem;
  });

  return cy;
}

function renderParagraphPaginated(
  doc,
  content,
  margin,
  y,
  contentWidth,
  layout,
  maxY,
  startNewPage,
  contentTop,
  initialGapBefore = 0,
  reserveBottom = 0,
) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(layout.bodyFontSize);
  doc.setTextColor(0, 0, 0);

  const rows = doc.splitTextToSize(content, contentWidth);
  const lineHeight = pdfLineHeightMm(doc);
  let cy = y;
  let pendingGap = initialGapBefore;
  const pageBottom = Math.max(
    contentTop + lineHeight,
    maxY - Math.max(0, reserveBottom),
  );

  rows.forEach((row, rowIndex) => {
    if (cy + pendingGap + lineHeight > pageBottom && cy > contentTop + 0.5) {
      startNewPage();
      cy = contentTop;
      pendingGap = 0;
    }
    cy += pendingGap;
    pendingGap = 0;
    doc.text(row, margin, cy);
    cy += lineHeight;
  });

  return cy;
}

function renderMetaBlockPaginated(
  doc,
  content,
  margin,
  y,
  contentWidth,
  layout,
  maxY,
  startNewPage,
  contentTop,
  initialGapBefore = 0,
) {
  const idx = content.indexOf(':');
  const label = content.slice(0, idx + 1);
  const value = content.slice(idx + 1).trim();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(layout.metaFontSize);
  doc.setTextColor(0, 0, 0);

  const labelW = doc.getTextWidth(label);
  doc.setFont('helvetica', 'normal');
  const valueLines = doc.splitTextToSize(value ? ` ${value}` : '', contentWidth - labelW - 2);
  const lineHeight = pdfLineHeightMm(doc);
  const totalH = Math.max(lineHeight, valueLines.length * lineHeight);

  if (y + initialGapBefore + totalH > maxY && y > contentTop + 0.5) {
    startNewPage();
    y = contentTop;
    initialGapBefore = 0;
  }

  y += initialGapBefore;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(layout.metaFontSize);
  doc.text(label, margin, y);
  doc.setFont('helvetica', 'normal');

  let cy = y;
  valueLines.forEach((line, lineIndex) => {
    if (lineIndex > 0 && cy + lineHeight > maxY) {
      startNewPage();
      cy = contentTop;
    }
    doc.text(line, margin + (lineIndex === 0 ? labelW : 0), cy);
    cy += lineHeight;
  });

  doc.setFontSize(layout.bodyFontSize);
  return cy;
}

function renderMetaBlock(doc, content, margin, y, contentWidth, layout) {
  const idx = content.indexOf(':');
  const label = content.slice(0, idx + 1);
  const value = content.slice(idx + 1).trim();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(layout.metaFontSize);
  doc.setTextColor(0, 0, 0);
  doc.text(label, margin, y);

  const labelW = doc.getTextWidth(label);
  doc.setFont('helvetica', 'normal');
  const valueLines = doc.splitTextToSize(value ? ` ${value}` : '', contentWidth - labelW);
  doc.text(valueLines, margin + labelW, y);

  return y + valueLines.length * pdfLineHeightMm(doc);
}

function renderHeadingBlock(doc, content, margin, y, contentWidth, layout) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(layout.headingFontSize);
  doc.setTextColor(0, 0, 0);
  const rows = doc.splitTextToSize(content, contentWidth);
  const lh = pdfLineHeightMm(doc);
  doc.text(rows, margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(layout.bodyFontSize);
  return y + rows.length * lh;
}

function renderParagraphBlock(doc, content, margin, y, contentWidth, layout) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(layout.bodyFontSize);
  doc.setTextColor(0, 0, 0);
  const rows = doc.splitTextToSize(content, contentWidth);
  doc.text(rows, margin, y);
  return y + rows.length * pdfLineHeightMm(doc);
}

function renderBulletsBlock(doc, items, margin, y, contentWidth, layout) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(layout.bodyFontSize);
  doc.setTextColor(0, 0, 0);
  doc.setFillColor(0, 0, 0);

  const textX = margin + PDF_BULLET_LAYOUT.textXOffset;
  const bulletX = margin + PDF_BULLET_LAYOUT.bulletXOffset;
  const textWidth = pdfBulletTextWidth(contentWidth);
  const lineHeight = pdfLineHeightMm(doc);

  let cy = y;
  items.forEach((item, i) => {
    const rows = doc.splitTextToSize(item, textWidth);
    doc.circle(bulletX, pdfBulletCenterY(doc, cy), PDF_BULLET_LAYOUT.bulletRadius, 'F');
    doc.text(rows, textX, cy);
    cy += rows.length * lineHeight;
    if (i < items.length - 1) cy += layout.gapBulletItem;
  });

  return cy;
}

function renderBlock(doc, block, margin, y, contentWidth, layout) {
  if (block.type === 'break') return y + layout.gapBreak;
  if (block.type === 'meta') return renderMetaBlock(doc, block.content, margin, y, contentWidth, layout);
  if (block.type === 'heading') return renderHeadingBlock(doc, block.content, margin, y, contentWidth, layout);
  if (block.type === 'paragraph') return renderParagraphBlock(doc, block.content, margin, y, contentWidth, layout);
  if (block.type === 'bullets') return renderBulletsBlock(doc, block.items, margin, y, contentWidth, layout);
  return y;
}

/** Build export blocks from rich HTML (preserves heading/list structure, no raster lines). */
function structureJdBlocksFromRichHtml(html) {
  if (typeof DOMParser === 'undefined') {
    return structureJdBlocks(String(html || '').replace(/<[^>]+>/g, '\n'));
  }

  const doc = new DOMParser().parseFromString(`<div id="__jd_blocks__">${html}</div>`, 'text/html');
  const root = doc.getElementById('__jd_blocks__');
  if (!root) return [];

  const blocks = [];
  const getText = (el) => (el.textContent || '').replace(/\s+/g, ' ').trim();
  const elements = root.querySelectorAll('p, ul, ol, h1, h2, h3, h4, h5, h6, blockquote');

  elements.forEach((el) => {
    if (el.tagName !== 'UL' && el.tagName !== 'OL' && el.parentElement?.closest('li')) return;
    if (['UL', 'OL'].includes(el.tagName) && el.parentElement?.closest('li')) return;

    const text = getText(el);
    if (!text && !['UL', 'OL'].includes(el.tagName)) return;

    if (el.tagName === 'UL' || el.tagName === 'OL') {
      const items = [...el.children]
        .filter((c) => c.tagName === 'LI')
        .map((li) => getText(li))
        .filter(Boolean);
      if (items.length) blocks.push({ type: 'bullets', items });
      return;
    }

    if (isJdMetaLine(text) || el.classList.contains('jd-preview-meta')) {
      if (!isSectionDetailLine(text)) {
        blocks.push({ type: 'meta', content: text });
      }
      return;
    }

    const isHeading = el.classList.contains('jd-preview-heading')
      || /^H[1-6]$/.test(el.tagName)
      || (el.tagName === 'P' && el.children.length === 1 && el.children[0].tagName === 'STRONG'
        && isSectionHeading((el.children[0].textContent || '').trim()))
      || isSectionHeading(text);

    if (isHeading) {
      blocks.push({ type: 'heading', content: text.replace(/:$/, '') });
      return;
    }

    blocks.push({ type: 'paragraph', content: text });
  });

  return blocks;
}

function renderJdBlocksIntoPdf(doc, blocks, logoData, layoutOverrides = {}) {
  const layout = { ...JD_PDF_LAYOUT, ...layoutOverrides };
  doc.setLineHeightFactor(layout.lineHeightFactor);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = layout.marginMm;
  const contentWidth = pageWidth - margin * 2;
  const contentTop = layout.headerHeightMm + 5;
  const maxY = pageHeight - layout.footerMarginMm;
  const pdfBlocks = normalizePdfBlocks(blocks);

  let pageNum = 1;
  let y = contentTop;
  drawFeujiPdfHeader(doc, logoData, layout.headerHeightMm);

  const drawFooter = () => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(String(pageNum), pageWidth / 2, pageHeight - 4, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  };

  const startNewPage = () => {
    drawFooter();
    doc.addPage();
    pageNum += 1;
    drawFeujiPdfHeader(doc, logoData, layout.headerHeightMm);
    y = contentTop;
  };

  pdfBlocks.forEach((block, index) => {
    const prevBlock = index > 0 ? pdfBlocks[index - 1] : null;
    const nextBlock = index < pdfBlocks.length - 1 ? pdfBlocks[index + 1] : null;

    if (block.type === 'break') {
      y += layout.gapBreak;
      return;
    }

    if (block.type === 'section') {
      const nextSectionReserve = computeNextSectionReserve(
        doc, nextBlock, block, contentWidth, layout,
      );
      y = renderSectionBlock(
        doc,
        block,
        prevBlock,
        nextBlock,
        margin,
        y,
        contentWidth,
        layout,
        maxY,
        startNewPage,
        contentTop,
        nextSectionReserve,
      );
      return;
    }

    const gapBefore = gapBeforeBlock(block, prevBlock, layout);
    const gapAfter = gapAfterBlock(block, nextBlock, layout);
    const nextSectionReserve = computeNextSectionReserve(
      doc, nextBlock, block, contentWidth, layout,
    );

    if (block.type === 'bullets') {
      y = renderBulletsPaginated(
        doc,
        block.items,
        margin,
        y,
        contentWidth,
        layout,
        maxY,
        startNewPage,
        contentTop,
        gapBefore,
        nextSectionReserve,
      );
      y += gapAfter;
      return;
    }

    if (block.type === 'paragraph') {
      y = renderParagraphPaginated(
        doc,
        block.content,
        margin,
        y,
        contentWidth,
        layout,
        maxY,
        startNewPage,
        contentTop,
        gapBefore,
      );
      y += gapAfter;
      return;
    }

    if (block.type === 'meta') {
      y = renderMetaBlockPaginated(
        doc,
        block.content,
        margin,
        y,
        contentWidth,
        layout,
        maxY,
        startNewPage,
        contentTop,
        gapBefore,
      );
      y += gapAfter;
      return;
    }

    if (block.type === 'heading') {
      if (y + gapBefore + estimateHeadingHeight(doc, block.content, contentWidth, layout) > maxY) {
        startNewPage();
      }
      y += gapBefore;
      y = renderHeadingBlock(doc, block.content, margin, y, contentWidth, layout);
      y += gapAfter;
      return;
    }

    const blockH = estimateBlockHeight(doc, block, contentWidth, layout);
    if (y + gapBefore + blockH + gapAfter > maxY) startNewPage();

    y += gapBefore;
    y = renderBlock(doc, block, margin, y, contentWidth, layout);
    y += gapAfter;
  });

  drawFooter();
}

/** Render a full JD into an existing jsPDF document with structured field spacing. */
export function renderJdIntoPdf(doc, jdText, logoData, layoutOverrides = {}) {
  renderJdBlocksIntoPdf(doc, structureJdBlocks(jdText), logoData, layoutOverrides);
}

/** Convert structured JD blocks to HTML body for Word export. */
export function jdPlainTextToWordBodyHtml(jdText) {
  const blocks = structureJdBlocks(jdText);
  const parts = [];
  let metaOpen = false;

  const closeMeta = () => {
    if (metaOpen) {
      parts.push('</div>');
      metaOpen = false;
    }
  };

  blocks.forEach((block, index) => {
    const prev = index > 0 ? blocks[index - 1] : null;
    const next = index < blocks.length - 1 ? blocks[index + 1] : null;

    if (block.type === 'break') {
      closeMeta();
      parts.push('<p style="margin:0;height:6px;font-size:0;line-height:0;">&nbsp;</p>');
      return;
    }

    if (block.type === 'meta') {
      if (!metaOpen) {
        closeMeta();
        const topGap = prev && prev.type !== 'meta' ? 'margin-top:0;' : '';
        parts.push(`<div class="jd-meta-block" style="${topGap}margin-bottom:0;padding:0 0 2px 0;">`);
        metaOpen = true;
      }
      const idx = block.content.indexOf(':');
      const label = block.content.slice(0, idx + 1);
      const value = block.content.slice(idx + 1).trim();
      const bottom = next?.type === 'meta' ? '4px' : '0';
      parts.push(
        `<p style="margin:0 0 ${bottom};font-size:10.5pt;line-height:1.35;color:#000;">` +
        `<strong style="font-weight:700;">${escapeHtml(label)}</strong> ${escapeHtml(value)}</p>`
      );
      if (next?.type !== 'meta') {
        parts.push('</div>');
        parts.push('<p style="margin:0 0 10px;font-size:0;line-height:0;">&nbsp;</p>');
        metaOpen = false;
      }
      return;
    }

    closeMeta();

    if (block.type === 'heading') {
      const marginTop = prev?.type === 'meta' ? '12px' : prev ? '14px' : '4px';
      parts.push(
        `<p style="margin:${marginTop} 0 6px;font-size:11.5pt;font-weight:bold;line-height:1.3;color:#000;">${escapeHtml(block.content)}</p>`
      );
      return;
    }

    if (block.type === 'paragraph') {
      const marginTop = prev?.type === 'heading' ? '4px' : '0';
      parts.push(
        `<p style="margin:${marginTop} 0 6px;font-size:10.5pt;line-height:1.4;color:#000;text-align:justify;">` +
        `${escapeHtml(block.content)}</p>`
      );
      return;
    }

    if (block.type === 'bullets') {
      const marginTop = prev?.type === 'heading' ? '4px' : '0';
      parts.push(
        `<ul style="margin:${marginTop} 0 10px 22px;padding:0;font-size:10.5pt;line-height:1.4;color:#000;list-style-type:disc;">`
      );
      block.items.forEach((item) => {
        parts.push(`<li style="margin:0 0 4px;">${escapeHtml(item)}</li>`);
      });
      parts.push('</ul>');
    }
  });

  closeMeta();
  return parts.join('\n');
}

export function buildJdWordDocumentHtml(jdText, logoData) {
  const richBody = prepareJdRichHtmlForExport(jdText);
  const body = richBody || jdPlainTextToWordBodyHtml(jdText);
  const logoBase64 = typeof logoData === 'string' ? logoData : logoData?.dataUrl || '';
  const logoAspectRatio = typeof logoData === 'object' ? logoData?.aspectRatio : null;
  const header = buildFeujiWordHeaderHtml(logoBase64, logoAspectRatio);

  return `<html xmlns:v="urn:schemas-microsoft-com:vml"
xmlns:o="urn:schemas-microsoft-com:office:office"
xmlns:w="urn:schemas-microsoft-com:office:word"
xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=utf-8">
<meta name="ProgId" content="Word.Document">
<meta name="Generator" content="Microsoft Word 15">
<!--[if gte mso 9]><xml>
<w:WordDocument>
<w:View>Print</w:View>
<w:Zoom>100</w:Zoom>
<w:DoNotOptimizeForBrowser/>
</w:WordDocument>
</xml><![endif]-->
<style>
@page WordSection1 {
  size: 8.5in 11in;
  margin: 0.5in 0.8in 0.8in 0.8in;
}
div.WordSection1 { page: WordSection1; }
body { font-family: Arial, sans-serif; font-size: 10.5pt; margin: 0; padding: 0; color: ${FEUJI_BRAND.bodyTextHex}; background: #FFFFFF; }
${JD_WORD_EXPORT_CSS}
${FEUJI_WORD_HEADER_CSS}
</style>
</head>
<body lang="EN-US" style="color:${FEUJI_BRAND.bodyTextHex};background:#FFFFFF;">
<div class="WordSection1">
${header}
<div class="jd-content jd-preview-content" style="font-family:Arial,sans-serif;color:${FEUJI_BRAND.bodyTextHex};">
${body}
</div>
</div>
</body>
</html>`;
}

/** Word export layout — spacing only, no lines/borders (preview CSS not reused). */
const JD_WORD_EXPORT_CSS = `
.jd-content { text-align: left; font-family: Arial, sans-serif; color: ${FEUJI_BRAND.bodyTextHex}; }
.jd-content p { margin: 0 0 0.65em; border: none !important; color: ${FEUJI_BRAND.bodyTextHex}; mso-color-alt: windowtext; }
.jd-content p.jd-preview-meta { margin: 0 0 0.35em; color: ${FEUJI_BRAND.bodyTextHex}; }
.jd-content p.jd-preview-meta strong { font-weight: 700; color: ${FEUJI_BRAND.metaLabelHex}; }
.jd-content p.jd-preview-heading {
  margin-top: 1em; margin-bottom: 0.5em; font-weight: 700; font-size: 11.5pt; border: none !important;
  color: ${FEUJI_BRAND.sectionHeadingHex};
}
.jd-content ul, .jd-content ol { margin: 0.25em 0 0.85em 1.25em; padding-left: 1.15em; border: none !important; color: ${FEUJI_BRAND.bodyTextHex}; }
.jd-content ul { list-style-type: disc; }
.jd-content ol { list-style-type: decimal; }
.jd-content li { margin-bottom: 0.4em; line-height: 1.55; border: none !important; color: ${FEUJI_BRAND.bodyTextHex}; }
.jd-content strong, .jd-content b { font-weight: 700; color: ${FEUJI_BRAND.bodyTextHex}; }
.jd-content em, .jd-content i { font-style: italic; color: ${FEUJI_BRAND.bodyTextHex}; }
.jd-content h1, .jd-content h2, .jd-content h3, .jd-content h4, .jd-content h5, .jd-content h6 {
  margin: 0.9em 0 0.45em; font-weight: 700; border: none !important; color: ${FEUJI_BRAND.sectionHeadingHex};
}
.jd-content span { color: ${FEUJI_BRAND.bodyTextHex}; }
.jd-content hr { display: none !important; height: 0 !important; margin: 0 !important; border: none !important; }
.jd-content *, .jd-content table, .jd-content tr, .jd-content td { border: none !important; box-shadow: none !important; }
`;

const FEUJI_WORD_HEADER_CSS = `
.feuji-word-header { width: 100%; border-collapse: collapse; }
.feuji-word-header td { border: 0; }
`;

function stripBorderStyles(styleText) {
  return String(styleText || '')
    .replace(/border(?:-top|-right|-bottom|-left)?\s*:[^;"]*;?/gi, '')
    .replace(/box-shadow\s*:[^;"]*;?/gi, '')
    .replace(/outline\s*:[^;"]*;?/gi, '')
    .replace(/background-image\s*:[^;"]*;?/gi, '')
    .trim();
}

function removeSeparatorElements(root) {
  root.querySelectorAll('hr').forEach((el) => el.remove());
  root.querySelectorAll('p, div, span, tr').forEach((el) => {
    const style = (el.getAttribute('style') || '').toLowerCase();
    const text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
    const hasContent = el.querySelector('img, ul, ol, table, li');
    if (/height\s*:\s*[12]px/i.test(style)) {
      el.remove();
      return;
    }
    if (!text && !hasContent && (/border/i.test(style) || el.tagName === 'HR')) {
      el.remove();
    }
  });
}

function prepareJdRichHtmlForExport(jdText) {
  let html = prepareJdRichHtml(jdText).replace(/<hr\b[^>]*\/?>/gi, '');
  if (typeof DOMParser === 'undefined') {
    return html.replace(/\sstyle="([^"]*)"/gi, (_m, style) => {
      const cleaned = stripBorderStyles(style);
      return cleaned ? ` style="${cleaned}"` : '';
    });
  }

  const doc = new DOMParser().parseFromString(`<div id="__jd_export__">${html}</div>`, 'text/html');
  const root = doc.getElementById('__jd_export__');
  if (!root) return html;

  removeSeparatorElements(root);
  root.querySelectorAll('*').forEach((el) => {
    el.removeAttribute('border');
    if (el.hasAttribute('style')) {
      const cleaned = stripBorderStyles(el.getAttribute('style'));
      if (cleaned) el.setAttribute('style', cleaned);
      else el.removeAttribute('style');
    }
  });

  root.querySelectorAll('p, li, span, div, strong, b, em, i, h1, h2, h3, h4, h5, h6').forEach((el) => {
    const style = el.getAttribute('style') || '';
    const withoutColor = style.replace(/(^|;)color\s*:\s*[^;"]+/gi, '').replace(/^;|;$/g, '').trim();
    const nextStyle = withoutColor ? `${withoutColor};color:${FEUJI_BRAND.bodyTextHex}` : `color:${FEUJI_BRAND.bodyTextHex}`;
    el.setAttribute('style', nextStyle);
  });

  return root.innerHTML.trim();
}

/** Vector PDF export — no html2canvas, no horizontal seam/border lines. */
export async function renderJdRichHtmlToPdf(jdText, logoData) {
  const doc = new jsPDF();
  const blocks = resolveJdExportBlocks(jdText);
  if (blocks.length) {
    renderJdBlocksIntoPdf(doc, blocks, logoData);
  } else if (String(jdText || '').trim()) {
    renderJdIntoPdf(doc, jdText, logoData);
  }
  return doc;
}

const LETTER_WIDTH_MM = 215.9;
const DOCX_BLACK = '000000';

function mmToTwip(mm) {
  return Math.round(mm * 56.6929133858);
}

function dataUrlToUint8Array(dataUrl) {
  const base64 = String(dataUrl || '').split(',')[1] || '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function loadImageElement(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/** Canvas header banner — mirrors drawFeujiPdfHeader (orange bar + right-aligned logo). */
export async function renderFeujiHeaderBannerPng(logoData, widthMm, heightMm) {
  const scale = 3;
  const widthPx = (widthMm / 25.4) * 96;
  const heightPx = (heightMm / 25.4) * 96;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(widthPx * scale);
  canvas.height = Math.round(heightPx * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);

  const [r, g, b] = FEUJI_BRAND.headerOrangeRgb;
  ctx.fillStyle = `rgb(${r},${g},${b})`;
  ctx.fillRect(0, 0, widthPx, heightPx);

  let drewLogo = false;
  if (logoData?.dataUrl && logoData.aspectRatio) {
    try {
      const img = await loadImageElement(logoData.dataUrl);
      const logoH = heightPx;
      const logoW = logoH * logoData.aspectRatio;
      const logoX = widthPx - logoW - (1.5 / 25.4) * 96;
      ctx.drawImage(img, logoX, 0, logoW, logoH);
      drewLogo = true;
    } catch {
      drewLogo = false;
    }
  }

  if (!drewLogo) {
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold italic 12px Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText('feuji', widthPx - 4, heightPx / 2);
  }

  return canvas.toDataURL('image/png');
}

function resolveJdExportBlocks(jdText) {
  const richHtml = prepareJdRichHtmlForExport(jdText);
  let blocks = richHtml.trim() ? structureJdBlocksFromRichHtml(richHtml) : [];
  if (!blocks.length) blocks = structureJdBlocks(jdText);
  return blocks;
}

function docxTextRun(text, { bold = false, sizeHalfPt = 20 } = {}) {
  return new TextRun({
    text,
    font: 'Arial',
    size: sizeHalfPt,
    bold,
    color: DOCX_BLACK,
  });
}

function buildDocxChildrenFromJdBlocks(blocks, layout) {
  const children = [];

  blocks.forEach((block, index) => {
    const prev = index > 0 ? blocks[index - 1] : null;
    const next = index < blocks.length - 1 ? blocks[index + 1] : null;

    if (block.type === 'break') {
      children.push(new Paragraph({ spacing: { after: mmToTwip(layout.gapBreak) } }));
      return;
    }

    const before = mmToTwip(gapBeforeBlock(block, prev, layout));
    const after = mmToTwip(gapAfterBlock(block, next, layout));

    if (block.type === 'meta') {
      const idx = block.content.indexOf(':');
      const runs = [];
      if (idx === -1) {
        runs.push(docxTextRun(block.content, { bold: true }));
      } else {
        runs.push(docxTextRun(block.content.slice(0, idx + 1), { bold: true }));
        const value = block.content.slice(idx + 1).trim();
        if (value) runs.push(docxTextRun(` ${value}`));
      }
      children.push(new Paragraph({ children: runs, spacing: { before, after } }));
      return;
    }

    if (block.type === 'heading') {
      children.push(new Paragraph({
        children: [docxTextRun(block.content, { bold: true, sizeHalfPt: 23 })],
        spacing: { before, after },
      }));
      return;
    }

    if (block.type === 'paragraph') {
      children.push(new Paragraph({
        children: [docxTextRun(block.content)],
        alignment: AlignmentType.JUSTIFIED,
        spacing: { before, after },
      }));
      return;
    }

    if (block.type === 'bullets') {
      block.items.forEach((item, itemIndex) => {
        const itemBefore = itemIndex === 0 ? before : 0;
        const itemAfter = itemIndex < block.items.length - 1
          ? mmToTwip(layout.gapBulletItem)
          : after;
        children.push(new Paragraph({
          children: [docxTextRun(`• ${item}`)],
          indent: {
            left: convertInchesToTwip(0.2),
            hanging: convertInchesToTwip(0.15),
          },
          spacing: { before: itemBefore, after: itemAfter },
        }));
      });
    }
  });

  return children;
}

/**
 * Word export — same block pipeline and header branding as PDF (client-side docx).
 * Header is a body image (not Word header/footer) so edits / Save-as-PDF stay stable.
 */
export async function renderJdToDocxBlob(jdText, logoData) {
  const layout = { ...JD_PDF_LAYOUT };
  const blocks = resolveJdExportBlocks(jdText);
  const marginInches = layout.marginMm / 25.4;
  const contentWidthMm = LETTER_WIDTH_MM - 2 * layout.marginMm;
  const headerHeightMm = layout.headerHeightMm;

  const headerPng = await renderFeujiHeaderBannerPng(logoData, contentWidthMm, headerHeightMm);
  const headerBytes = dataUrlToUint8Array(headerPng);
  const imgWidthPx = Math.round((contentWidthMm / 25.4) * 96);
  const imgHeightPx = Math.round((headerHeightMm / 25.4) * 96);

  const children = [
    new Paragraph({
      children: [
        new ImageRun({
          data: headerBytes,
          transformation: { width: imgWidthPx, height: imgHeightPx },
        }),
      ],
      spacing: { after: mmToTwip(3) },
    }),
    ...buildDocxChildrenFromJdBlocks(blocks, layout),
  ];

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(0.2),
            bottom: convertInchesToTwip(marginInches),
            left: convertInchesToTwip(marginInches),
            right: convertInchesToTwip(marginInches),
          },
        },
      },
      children,
    }],
  });

  return Packer.toBlob(doc);
}
